import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Download, Plus, X, ClipboardPaste } from "lucide-react";
import { crmApi, crmToken } from "../../lib/crmApi";
import {
  PageHead, Chip, Field, inputCls,
  SERVICE_CATEGORIES, LEAD_STATUSES, LEAD_SCORES, label,
} from "./crmUi";

const EMPTY = {
  customer_name: "", whatsapp_number: "", location: "", source_page: "",
  source_channel: "whatsapp", service_category: "", sub_category: "",
  property_type: "", requirement_summary: "", budget_level: "", urgency: "",
  lead_score: "warm", lead_status: "new",
};

export default function LeadsPage() {
  const [params, setParams] = useSearchParams();
  const [leads, setLeads] = useState(null);
  const [staff, setStaff] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [paste, setPaste] = useState({ customer_name: "", whatsapp_number: "", location: "", pasted_text: "" });
  const [pasteBusy, setPasteBusy] = useState(false);
  const [pasteErr, setPasteErr] = useState("");
  const navigate = useNavigate();

  const submitPaste = async () => {
    if (!paste.pasted_text.trim()) return;
    setPasteBusy(true); setPasteErr("");
    try {
      const r = await crmApi.manualWaLead({ ...paste, pasted_text: paste.pasted_text.trim() });
      setShowPaste(false);
      setPaste({ customer_name: "", whatsapp_number: "", location: "", pasted_text: "" });
      navigate(`/admin/crm/leads/${r.lead.id}`);
    } catch (e) { setPasteErr(e.message); }
    finally { setPasteBusy(false); }
  };

  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState(params.get("search") || "");

  const filters = {
    status: params.get("status") || "",
    score: params.get("score") || "",
    category: params.get("category") || "",
    assigned_to: params.get("assigned_to") || "",
    unassigned: params.get("unassigned") || "",
    source: params.get("source") || "",
    search: params.get("search") || "",
  };

  const reload = useCallback(() => {
    crmApi.leads(filters).then((d) => setLeads(d.leads)).catch((e) => setErr(e.message));
  }, [params]); // eslint-disable-line

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { crmApi.staff().then((d) => setStaff(d.staff)).catch(() => {}); }, []);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  const create = async () => {
    setBusy(true); setErr("");
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await crmApi.createLead(body);
      setShowNew(false); setForm(EMPTY);
      reload();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const exportCsv = async () => {
    const res = await fetch(crmApi.exportCsvUrl(), { headers: { Authorization: `Bearer ${crmToken()}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "timefurniture-leads.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div data-testid="crm-leads-page">
      <PageHead eyebrow="Pipeline" title="Leads">
        <button
          type="button"
          onClick={exportCsv}
          data-testid="crm-leads-export"
          className="inline-flex items-center gap-2 border border-[#6E1F2C] text-[#6E1F2C] px-3 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC]"
        >
          <Download size={12} /> Export CSV
        </button>
        <button
          type="button"
          onClick={() => setShowPaste(true)}
          data-testid="crm-leads-paste-wa"
          className="inline-flex items-center gap-2 border border-[#3F6E2C] text-[#3F6E2C] px-3 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#3F6E2C] hover:text-white"
        >
          <ClipboardPaste size={12} /> Paste WhatsApp Lead
        </button>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          data-testid="crm-leads-new"
          className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#56151F]"
        >
          <Plus size={12} /> New Lead
        </button>
      </PageHead>

      {/* Filters */}
      <div className="bg-white border border-[#6E1F2C]/10 p-3 mb-4 flex flex-wrap gap-2 items-center">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && setFilter("search", search)}
          placeholder="Search name / number / ref…"
          data-testid="crm-leads-search"
          className="flex-1 min-w-[180px] bg-[#FAF5EC] border border-[#6E1F2C]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#6E1F2C]"
        />
        <select value={filters.status} onChange={(e) => setFilter("status", e.target.value)} data-testid="crm-filter-status" className="bg-[#FAF5EC] border border-[#6E1F2C]/15 px-2 py-2 text-xs">
          <option value="">All statuses</option>
          {LEAD_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={filters.score} onChange={(e) => setFilter("score", e.target.value)} data-testid="crm-filter-score" className="bg-[#FAF5EC] border border-[#6E1F2C]/15 px-2 py-2 text-xs">
          <option value="">All scores</option>
          {LEAD_SCORES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={filters.category} onChange={(e) => setFilter("category", e.target.value)} data-testid="crm-filter-category" className="bg-[#FAF5EC] border border-[#6E1F2C]/15 px-2 py-2 text-xs">
          <option value="">All services</option>
          {SERVICE_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => setFilter("source", e.target.value)} data-testid="crm-filter-source" className="bg-[#FAF5EC] border border-[#6E1F2C]/15 px-2 py-2 text-xs">
          <option value="">All sources</option>
          {["whatsapp", "website_form", "website", "referral", "walk_in", "phone", "other"].map((s) => (
            <option key={s} value={s}>{s.replace("_", " ")}</option>
          ))}
        </select>
        {staff.length > 0 && (
          <select value={filters.assigned_to} onChange={(e) => setFilter("assigned_to", e.target.value)} data-testid="crm-filter-assigned" className="bg-[#FAF5EC] border border-[#6E1F2C]/15 px-2 py-2 text-xs">
            <option value="">All salespeople</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>

      {err && <p className="text-xs text-[#A3141A] mb-2">{err}</p>}
      {!leads && <p className="text-xs text-[#7A6B5F]">Loading…</p>}
      {leads && leads.length === 0 && (
        <p className="text-sm text-[#7A6B5F] italic" data-testid="crm-leads-empty">No leads match. Create your first lead with “New Lead”.</p>
      )}
      {leads && leads.length > 0 && (
        <div className="overflow-x-auto bg-white border border-[#6E1F2C]/10">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-[9px] tracking-[0.18em] uppercase text-[#7A6B5F] border-b border-[#6E1F2C]/15 bg-[#FAF5EC]">
                <th className="px-3 py-2.5">Ref</th>
                <th className="px-3 py-2.5">Customer</th>
                <th className="px-3 py-2.5">Service</th>
                <th className="px-3 py-2.5">Score</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Salesperson</th>
                <th className="px-3 py-2.5">Next follow-up</th>
                <th className="px-3 py-2.5">Updated</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-[#6E1F2C]/8 hover:bg-[#FAF5EC]/60">
                  <td className="px-3 py-2.5">
                    <Link to={`/admin/crm/leads/${l.id}`} data-testid={`crm-lead-link-${l.id}`} className="text-[#6E1F2C] font-medium hover:underline underline-offset-2">
                      {l.lead_ref}
                    </Link>
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="text-[#1C110C] font-medium">
                      {l.customer_name || "Unknown"}
                      {l.possible_duplicate && (
                        <span data-testid={`crm-lead-dup-${l.id}`} className="ml-2 text-[8px] tracking-[0.12em] uppercase px-1.5 py-0.5 border border-[#A6822F]/50 text-[#A6822F] bg-[#A6822F]/10 align-middle">
                          Possible duplicate
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] text-[#7A6B5F]">{l.whatsapp_number || "—"}{l.location ? ` · ${l.location}` : ""}</p>
                  </td>
                  <td className="px-3 py-2.5 text-[#3E2A22]">{label(SERVICE_CATEGORIES, l.service_category)}</td>
                  <td className="px-3 py-2.5"><Chip list={LEAD_SCORES} value={l.lead_score} /></td>
                  <td className="px-3 py-2.5"><Chip list={LEAD_STATUSES} value={l.lead_status} /></td>
                  <td className="px-3 py-2.5 text-[#3E2A22]">{l.assigned_salesperson_name || <span className="text-[#A3141A]">Unassigned</span>}</td>
                  <td className="px-3 py-2.5 text-[#3E2A22]">{l.next_followup_date || "—"}</td>
                  <td className="px-3 py-2.5 text-[10px] text-[#7A6B5F]">{(l.updated_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paste WhatsApp lead modal */}
      {showPaste && (
        <div className="fixed inset-0 z-50 bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !pasteBusy && setShowPaste(false)}>
          <div className="bg-white w-full max-w-[620px] border border-[#6E1F2C]/20 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="crm-paste-modal">
            <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between">
              <h3 className="font-serif-display text-lg text-[#1C110C]">Paste WhatsApp Lead</h3>
              <button type="button" onClick={() => setShowPaste(false)} className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-[11px] text-[#7A6B5F]">Paste the customer's WhatsApp conversation or requirement below. AI will create the lead with a summary, score, suggested reply, follow-up task and salesperson suggestion.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Customer name"><input data-testid="crm-paste-name" className={inputCls} value={paste.customer_name} onChange={(e) => setPaste({ ...paste, customer_name: e.target.value })} /></Field>
                <Field label="WhatsApp number"><input data-testid="crm-paste-whatsapp" className={inputCls} value={paste.whatsapp_number} onChange={(e) => setPaste({ ...paste, whatsapp_number: e.target.value })} placeholder="+9715…" /></Field>
                <Field label="Location"><input className={inputCls} value={paste.location} onChange={(e) => setPaste({ ...paste, location: e.target.value })} /></Field>
              </div>
              <Field label="Pasted conversation / requirement">
                <textarea rows={7} data-testid="crm-paste-text" className={inputCls} value={paste.pasted_text} onChange={(e) => setPaste({ ...paste, pasted_text: e.target.value })} placeholder={"Customer: Hi, I need curtains for my new villa…\nUs: …"} />
              </Field>
              {pasteErr && <p className="text-xs text-[#A3141A]" data-testid="crm-paste-error">{pasteErr}</p>}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowPaste(false)} disabled={pasteBusy} className="border border-[#6E1F2C]/30 text-[#6E1F2C] px-4 py-2 text-[10px] tracking-[0.2em] uppercase">Cancel</button>
                <button type="button" onClick={submitPaste} disabled={pasteBusy || !paste.pasted_text.trim()} data-testid="crm-paste-save" className="bg-[#3F6E2C] text-white px-5 py-2 text-[10px] tracking-[0.2em] uppercase hover:bg-[#325a23] disabled:opacity-50">
                  {pasteBusy ? "AI analysing… (~20s)" : "Analyse & Create Lead"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New lead modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white w-full max-w-[620px] border border-[#6E1F2C]/20 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="crm-newlead-modal">
            <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between">
              <h3 className="font-serif-display text-lg text-[#1C110C]">New Lead</h3>
              <button type="button" onClick={() => setShowNew(false)} className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Customer name"><input data-testid="crm-newlead-name" className={inputCls} value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></Field>
              <Field label="WhatsApp number"><input data-testid="crm-newlead-whatsapp" className={inputCls} value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="+9715…" /></Field>
              <Field label="Location"><input className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Dubai Marina…" /></Field>
              <Field label="Source channel">
                <select className={inputCls} value={form.source_channel} onChange={(e) => setForm({ ...form, source_channel: e.target.value })}>
                  {["whatsapp", "website", "referral", "walk_in", "phone", "other"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              </Field>
              <Field label="Service category">
                <select data-testid="crm-newlead-category" className={inputCls} value={form.service_category} onChange={(e) => setForm({ ...form, service_category: e.target.value })}>
                  <option value="">— Select —</option>
                  {SERVICE_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </Field>
              <Field label="Sub category"><input className={inputCls} value={form.sub_category} onChange={(e) => setForm({ ...form, sub_category: e.target.value })} placeholder="Blackout curtains…" /></Field>
              <Field label="Property type">
                <select className={inputCls} value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })}>
                  <option value="">— Select —</option>
                  {["apartment", "villa", "townhouse", "office", "retail", "hotel", "other"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Source page"><input className={inputCls} value={form.source_page} onChange={(e) => setForm({ ...form, source_page: e.target.value })} placeholder="/curtains-dubai" /></Field>
              <Field label="Budget level">
                <select className={inputCls} value={form.budget_level} onChange={(e) => setForm({ ...form, budget_level: e.target.value })}>
                  <option value="">Unknown</option>
                  {["low", "medium", "high", "premium"].map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Urgency">
                <select className={inputCls} value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })}>
                  <option value="">Unknown</option>
                  {["immediate", "this_month", "flexible"].map((u) => <option key={u} value={u}>{u.replace("_", " ")}</option>)}
                </select>
              </Field>
              <Field label="Requirement summary" className="sm:col-span-2">
                <textarea data-testid="crm-newlead-requirement" rows={2} className={inputCls} value={form.requirement_summary} onChange={(e) => setForm({ ...form, requirement_summary: e.target.value })} />
              </Field>
              {err && <p className="text-xs text-[#A3141A] sm:col-span-2">{err}</p>}
              <div className="sm:col-span-2 flex justify-end gap-2">
                <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 text-[10px] tracking-[0.22em] uppercase text-[#7A6B5F]">Cancel</button>
                <button type="button" onClick={create} disabled={busy} data-testid="crm-newlead-save" className="bg-[#6E1F2C] text-[#FAF5EC] px-5 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#56151F] disabled:opacity-60">
                  {busy ? "Saving…" : "Create Lead"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
